import type { PlayerCommand } from '../sim/commands';
import type { Sim } from '../sim/sim';
import { applyMatchView, applyStateDelta, type MatchViewSnapshot } from './snapshot';
import type { ServerMessage } from './protocol';
import { OnlineClient, type ConnectionStatus } from './client';

export class OnlineMatchRuntime {
  private readonly unsubscribe: () => void;
  private readonly unsubscribeStatus: () => void;
  private currentSnapshot: MatchViewSnapshot | null = null;
  private currentSnapshotSeq = 0;
  private lastSnapshotAt = performance.now();
  private readonly interpolationMs = 100;
  private statusValue: ConnectionStatus;

  constructor(
    private readonly sim: Sim,
    private readonly client: OnlineClient,
    private readonly matchId: string,
    private readonly onEvents: () => void,
  ) {
    this.statusValue = client.status;
    this.unsubscribe = client.onMessage((message) => this.handle(message));
    this.unsubscribeStatus = client.onStatus((status) => { this.statusValue = status; });
  }

  get status(): ConnectionStatus { return this.statusValue; }

  get renderAlpha(): number {
    return Math.max(0, Math.min(1, (performance.now() - this.lastSnapshotAt) / this.interpolationMs));
  }

  dispatch(command: PlayerCommand): void {
    this.client.sendCommand(command);
  }

  concede(): void {
    this.client.concede();
  }

  dispose(): void {
    this.unsubscribe();
    this.unsubscribeStatus();
  }

  private handle(message: ServerMessage): void {
    if (message.type !== 'snapshot' || message.matchId !== this.matchId) return;
    if (message.full) {
      const oldPositions = this.capturePositions();
      this.currentSnapshot = message.state;
      this.currentSnapshotSeq = message.snapshotSeq;
      applyMatchView(this.sim, message.state);
      this.restoreInterpolationOrigins(oldPositions);
      this.lastSnapshotAt = performance.now();
      this.client.acknowledgeSnapshot(this.matchId, message.snapshotSeq);
      this.onEvents();
      return;
    }
    if (!this.currentSnapshot || message.delta.baseSnapshotSeq !== this.currentSnapshotSeq) {
      this.client.requestResync(this.matchId);
      return;
    }
    const oldPositions = this.capturePositions();
    this.currentSnapshot = applyStateDelta(this.currentSnapshot, message.delta);
    this.currentSnapshotSeq = message.snapshotSeq;
    applyMatchView(this.sim, this.currentSnapshot);
    this.restoreInterpolationOrigins(oldPositions);
    this.lastSnapshotAt = performance.now();
    this.client.acknowledgeSnapshot(this.matchId, message.snapshotSeq);
    this.onEvents();
  }

  private capturePositions(): Map<number, { x: number; y: number }> {
    const positions = new Map<number, { x: number; y: number }>();
    for (const entity of this.sim.state.squads.values()) positions.set(entity.id, { ...entity.pos });
    for (const entity of this.sim.state.workers.values()) positions.set(entity.id, { ...entity.pos });
    for (const entity of this.sim.state.projectiles.values()) positions.set(entity.id, { ...entity.pos });
    return positions;
  }

  private restoreInterpolationOrigins(oldPositions: Map<number, { x: number; y: number }>): void {
    for (const entity of this.sim.state.squads.values()) entity.prevPos = oldPositions.get(entity.id) ?? { ...entity.pos };
    for (const entity of this.sim.state.workers.values()) entity.prevPos = oldPositions.get(entity.id) ?? { ...entity.pos };
    for (const entity of this.sim.state.projectiles.values()) entity.prevPos = oldPositions.get(entity.id) ?? { ...entity.pos };
  }
}

export { OnlineClient };
